import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getConflict, mapApiConflictToUi } from '../../../services/sandbox.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/** What each one said about a claim in conflict, for the Compare slide-out (CT-5718). */
export function useSandboxConflict({ entityType, entityId, claimId } = {}) {
  const [conflict, setConflict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!claimId) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setStatus(null);
    try {
      const { body } = await getConflict({ entityType, entityId, claimId });
      if (requestId !== fetchIdRef.current) return;
      setConflict(body ? mapApiConflictToUi(body) : null);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setConflict(null);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load the conflict');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId, claimId]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ conflict, loading, error, notFound: status === 404, refetch: fetchData }),
    [conflict, loading, error, status, fetchData],
  );
}

export default useSandboxConflict;
